// Shared modal-dialog primitive.
//
// Portals through `usePortalRoot()` so the dialog renders inside the
// embed's bounding box when wrapped in `<EmbeddableChat>`, and against
// `document.body` for the standalone shell. Scrims the rest of the
// surface, traps focus inside the panel while it's open, and restores
// focus to the previously-focused element on close. Escape and
// backdrop-click both request a close via `onClose`; callers decide
// whether to honour the request (e.g. while a save is in-flight they
// can ignore it by wrapping `onClose` in a busy-guard before passing
// it down).
//
// Nested dialogs: every open dialog registers itself on a module-level
// LIFO `dialogStack`. A single `document`-scoped keydown listener
// routes Escape and Tab to the topmost entry only — so a nested dialog
// "wins" regardless of where focus currently sits, including the rare
// cases where focus has drifted to `document.body` (blur after a
// rejected backdrop click, autoFocus thrash, etc.). Tab-cycling
// refocuses the first focusable element when the active element is
// outside the top dialog's panel, keeping the trap honest.
//
// The DeleteConfirmDialog predates this file and carries the same
// behaviour inline. When we next touch it, the duplication collapses
// here.

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { useFormFactor, useRegularWidth } from "~/hooks/useFormFactor";
import { isEmbeddedPortal } from "~/lib/portal-positioning";

/** On a phone every dialog is a bottom sheet: full width, top corners
 *  only, opaque (a raised tint over the app surface; the shared dialog
 *  token is 98% alpha and lets the timeline ghost through). */
const MOBILE_SHEET_PANEL: CSSProperties = {
  width: "100%",
  maxWidth: "100%",
  maxHeight: "calc(100dvh - env(safe-area-inset-top, 0px) - var(--chat-space-6))",
  borderRadius: "var(--chat-radius-xl) var(--chat-radius-xl) 0 0",
  border: "none",
  boxShadow: "none",
  background:
    "linear-gradient(var(--chat-surface-raised), var(--chat-surface-raised)), var(--chat-surface-app)",
  paddingBottom: "env(safe-area-inset-bottom, 0px)",
};

/** On tablet widths the mobile shell presents dialogs the way iPadOS does
 *  form sheets: a centred, fully rounded, opaque card of reading width. */
const MOBILE_FORM_SHEET_PANEL: CSSProperties = {
  width: "min(540px, calc(100% - var(--chat-space-8)))",
  maxWidth: "min(540px, calc(100% - var(--chat-space-8)))",
  maxHeight: "calc(100dvh - var(--chat-space-16))",
  borderRadius: "var(--chat-radius-xl)",
  border: "none",
  boxShadow: "var(--chat-shadow-dialog)",
  background: MOBILE_SHEET_PANEL.background,
};

/** Pixels a downward drag on the grabber must travel to dismiss. */
const SHEET_DISMISS_DRAG_PX = 80;

/**
 * Module-level stack of open dialogs. The most-recently-opened
 * dialog sits at the top. A single `document`-scoped keydown
 * listener services the stack — only the top dialog handles the
 * event, which gives us "innermost wins" nested-dialog semantics
 * without losing the document-level hook (the alternative —
 * attaching per-panel — broke Escape whenever focus drifted to
 * `document.body`, which happens routinely when a backdrop click
 * is intercepted or focus restores after an input blur).
 */
const dialogStack: Array<{
  panel: HTMLElement;
  close: () => void;
}> = [];

function installGlobalKeyHandler(doc: Document): () => void {
  function onKey(e: KeyboardEvent) {
    const top = dialogStack[dialogStack.length - 1];
    if (!top) return;
    // A portalled @nurion/ui Select list owns its own Escape and arrow keys.
    if (e.target instanceof Element && e.target.closest('[data-slot="select-content"]')) return;
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      top.close();
      return;
    }
    if (e.key !== "Tab") return;
    const focusable = top.panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    // If focus has drifted outside the top dialog (e.g. user clicked
    // the backdrop), Tab from nowhere lands back on the first
    // focusable element. This keeps the trap honest.
    const active = doc.activeElement as HTMLElement | null;
    if (!top.panel.contains(active)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }
  doc.addEventListener("keydown", onKey);
  return () => doc.removeEventListener("keydown", onKey);
}

// The global listener is installed once per document. The module
// tracks installation state so multiple dialogs don't pile up
// listeners (StrictMode mount / unmount / remount would otherwise
// multiply them).
let globalUninstall: (() => void) | null = null;
function ensureGlobalHandler(): void {
  if (typeof document === "undefined") return;
  if (globalUninstall) return;
  globalUninstall = installGlobalKeyHandler(document);
}

export interface DialogProps {
  /** Required accessible name. Rendered as `aria-labelledby` when
   *  `labelledBy` is supplied instead. */
  label?: string;
  /** Optional id of the element that labels the dialog. Preferred
   *  when the title is already a visible `<h2>`. */
  labelledBy?: string;
  /** Optional id of the element that describes the dialog. */
  describedBy?: string;
  /** Close request — Escape, backdrop click, or explicit close button.
   *  Callers that need to reject the close (busy state, in-flight
   *  save) should guard their own handler. */
  onClose: () => void;
  /** Width of the dialog panel in CSS. Defaults to 480px (brief). */
  width?: string;
  /** Extra panel styles, merged after the defaults. */
  panelStyle?: CSSProperties;
  /** When false, a backdrop click does not request close (Escape still
   *  routes through `onClose`). Used by auth prompts that must not
   *  vanish on an accidental outside click. Defaults true. */
  dismissOnBackdrop?: boolean;
  /** Where the panel sits within the scrim. `"center"` (default) keeps
   *  the classic centred modal; `"bottom"` anchors the panel to the
   *  bottom edge for a mobile bottom-sheet. */
  align?: "center" | "bottom";
  /** When false, the phone sheet has no drag grabber; task sheets that
   *  carry their own close control opt out. Defaults true. */
  grabber?: boolean;
  children: ReactNode;
}

// Selector covers every natively focusable element plus explicitly
// tabbable ones. `[hidden]` inputs / disabled controls are excluded
// via the `:not()` filters so a file input used only for click
// triggers doesn't steal the trap.
export const FOCUSABLE_SELECTOR =
  'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), ' +
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), ' +
  'textarea:not([disabled]):not([tabindex="-1"]), ' +
  'select:not([disabled]):not([tabindex="-1"]), ' +
  '[contenteditable="true"]:not([tabindex="-1"]), ' +
  '[tabindex]:not([tabindex="-1"])';

/**
 * The modal lifecycle every dialog shares, whatever its layout: inert the
 * rest of the portal root, move focus into the panel, join the dialog
 * stack (Escape and the Tab trap go to the top entry only), and on close
 * restore the background and the previously focused element. The panel's
 * parent must be the scrim, a direct child of the portal root.
 */
export function useModalLifecycle(
  dialogRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  const portalRoot = usePortalRoot();

  // Capture the previously-focused element once, at mount. A lazy
  // `useState` initialiser is Strict-Mode safe (React re-runs render,
  // but lazy-init fires exactly once) where a render-phase ref
  // mutation would fire twice in dev. The trade-off is that we keep
  // the element in state — we never read it for render, only for
  // restore, so the state never rerenders anything downstream.
  const [previouslyFocused] = useState<HTMLElement | null>(() => {
    if (typeof document === "undefined") return null;
    return document.activeElement as HTMLElement | null;
  });

  // Focus into the panel on open and inert the background so a later autofocus
  // cannot reach past the modal; clear inert before restoring focus on close.
  useEffect(() => {
    const node = dialogRef.current;
    const inerted: Element[] = [];
    if (portalRoot) {
      // Skip open-modal backdrops via stack registration, not a subtree scan
      // (which also matches inline role=dialog); leave an already-inert child alone.
      const scrims = new Set<Element>();
      if (node?.parentElement) scrims.add(node.parentElement);
      for (const entry of dialogStack) {
        const s = entry.panel.parentElement;
        if (s) scrims.add(s);
      }
      for (const child of Array.from(portalRoot.children)) {
        if (scrims.has(child) || child.hasAttribute("inert")) continue;
        child.setAttribute("inert", "");
        child.setAttribute("aria-hidden", "true");
        inerted.push(child);
      }
    }
    if (node && !node.contains(document.activeElement)) {
      // A dialog with a field opens on the field, not on its close button.
      const first =
        node.querySelector<HTMLElement>(
          "input:not([type=hidden]):not([disabled]), textarea:not([disabled])",
        ) ?? node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      // Moving focus in is for assistive tech and the keyboard; it must not
      // paint a focus ring on a control nobody chose (WebKit draws one for
      // programmatic focus). Tab still shows the ring as usual.
      const options: FocusOptions & { focusVisible?: boolean } = { focusVisible: false };
      first?.focus(options);
    }

    return () => {
      for (const el of inerted) {
        el.removeAttribute("inert");
        el.removeAttribute("aria-hidden");
      }
      previouslyFocused?.focus?.();
    };
  }, [dialogRef, previouslyFocused, portalRoot]);

  // Keep `onClose` on a ref so the stack entry we register once
  // always delegates to the latest handler, even if parents
  // recompute the callback.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  // Register this dialog on the global stack so the document-level
  // key handler routes Escape/Tab to the topmost open dialog. The
  // stack is a LIFO: nested dialogs push on mount and pop on
  // unmount, so only the innermost dialog ever sees Escape.
  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return;
    ensureGlobalHandler();
    const entry = { panel: node, close: () => closeRef.current() };
    dialogStack.push(entry);
    return () => {
      const index = dialogStack.indexOf(entry);
      if (index >= 0) dialogStack.splice(index, 1);
    };
  }, [dialogRef]);
}

/**
 * Centred modal dialog. The scrim is presentational; `role="dialog"`
 * + `aria-modal="true"` live on the inner panel so AT users hear the
 * boundary.
 */
export function Dialog({
  label,
  labelledBy,
  describedBy,
  onClose,
  width,
  panelStyle,
  dismissOnBackdrop = true,
  align = "center",
  grabber = true,
  children,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const portalRoot = usePortalRoot();
  const isMobileShell = useFormFactor() === "mobile";
  const isRegularWidth = useRegularWidth();
  const isSheet = isMobileShell && !isRegularWidth;
  const isFormSheet = isMobileShell && isRegularWidth;
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartRef = useRef<number | null>(null);

  useModalLifecycle(dialogRef, onClose);

  if (!portalRoot) return null;

  // Embed-rooted portals position the scrim absolutely so it fills
  // the embed's bounding box rather than the viewport. Standalone
  // (body-rooted) portals stay viewport-fixed. The embed root carries
  // `position: relative` so an absolute child with `inset: 0` lays
  // out against it; `maxHeight` adapts to the same surface so a tall
  // dialog inside a short embed scrolls instead of overflowing.
  const isEmbedded = isEmbeddedPortal(portalRoot);
  const scrimPosition: "fixed" | "absolute" = isEmbedded ? "absolute" : "fixed";
  const panelMaxHeight = isEmbedded
    ? "calc(100% - var(--chat-space-8))"
    : "calc(100vh - var(--chat-space-8))";

  const panel = (
    <BackdropClickSurface
      onClose={onClose}
      position={scrimPosition}
      dismissOnBackdrop={dismissOnBackdrop}
      align={isSheet ? "bottom" : isFormSheet ? "center" : align}
    >
      <div
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: width ?? "var(--chat-dialog-width)",
          maxHeight: panelMaxHeight,
          borderRadius: "var(--chat-radius-xl)",
          border: "1px solid var(--chat-surface-border)",
          background: "var(--chat-surface-dialog)",
          boxShadow: "var(--chat-shadow-dialog)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          // The panel is only ever focused programmatically (fallback for a view
          // with no focusable control); as a container it must not draw the
          // browser default focus ring.
          outline: "none",
          ...panelStyle,
          ...(isSheet ? MOBILE_SHEET_PANEL : {}),
          ...(isFormSheet ? MOBILE_FORM_SHEET_PANEL : {}),
          ...(dragOffset > 0 ? { transform: `translateY(${dragOffset}px)` } : {}),
        }}
      >
        {isSheet && grabber && (
          <div
            aria-hidden="true"
            data-chat-sheet-grabber
            style={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "center",
              padding: "var(--chat-space-2) 0 var(--chat-space-1)",
              touchAction: "none",
            }}
            onPointerDown={(e) => {
              dragStartRef.current = e.clientY;
              e.currentTarget.setPointerCapture(e.pointerId);
            }}
            onPointerMove={(e) => {
              if (dragStartRef.current === null) return;
              setDragOffset(Math.max(0, e.clientY - dragStartRef.current));
            }}
            onPointerUp={() => {
              const dismiss = dragOffset > SHEET_DISMISS_DRAG_PX;
              dragStartRef.current = null;
              setDragOffset(0);
              if (dismiss) onClose();
            }}
            onPointerCancel={() => {
              dragStartRef.current = null;
              setDragOffset(0);
            }}
          >
            <span
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: "var(--chat-fg-muted)",
                opacity: 0.4,
              }}
            />
          </div>
        )}
        {children}
      </div>
    </BackdropClickSurface>
  );

  return createPortal(panel, portalRoot);
}

/**
 * Backdrop that only fires `onClose` when a click *begins and ends*
 * on the backdrop itself. A drag that starts inside an input and
 * releases over the backdrop — common when text-selection drags
 * accidentally escape the panel — must not close the dialog.
 */
function BackdropClickSurface({
  onClose,
  children,
  position,
  dismissOnBackdrop,
  align,
}: {
  onClose: () => void;
  children: ReactNode;
  /** `"fixed"` for body-portalled standalone, `"absolute"` for the
   *  embed root (already a positioned containing block). */
  position: "fixed" | "absolute";
  /** When false, a backdrop click is inert. */
  dismissOnBackdrop: boolean;
  align: "center" | "bottom";
}) {
  const downOnBackdropRef = useRef(false);
  return (
    <div
      style={{
        position,
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: align === "bottom" ? "flex-end" : "center",
        justifyContent: "center",
        background: "var(--chat-scrim-dialog)",
      }}
      onMouseDown={(e) => {
        downOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (!dismissOnBackdrop) return;
        if (e.target !== e.currentTarget) return;
        if (!downOnBackdropRef.current) return;
        downOnBackdropRef.current = false;
        onClose();
      }}
    >
      {children}
    </div>
  );
}
